import { Rndm } from './Rndm.js';
import { Ease } from './Ease.js';

export interface XY {
    x: number;
    y: number;
    [key: string]: any;
}

export const V2 = { //2d vectors (using {x, y})
    pp: Math.PI * 2,
    new: {
        set: function (a: XY): XY {
            return { x: a.x, y: a.y };
        },
        zero: function (): XY {
            return { x: 0, y: 0 };
        },
        random: function (scale = 1): XY {
            return { x: Rndm.float(scale), y: Rndm.float(scale) };
        },
        sub: function (a: XY, b: XY): XY {
            return { x: a.x - b.x, y: a.y - b.y };
        },
        sub_x: function (a: XY, x: number): XY {
            return { x: a.x - x, y: a.y };
        },
        sub_y: function (a: XY, y: number): XY {
            return { x: a.x, y: a.y - y };
        },
        add: function (a: XY, b: XY): XY {
            return { x: a.x + b.x, y: a.y + b.y };
        },
        add_x: function (a: XY, x: number): XY {
            return { x: a.x + x, y: a.y };
        },
        add_y: function (a: XY, y: number): XY {
            return { x: a.x, y: a.y + y };
        },
        multiply: function (a: XY, b: XY): XY {
            return { x: a.x * b.x, y: a.y * b.y };
        },
        divide: function (a: XY, b: XY): XY {
            return { x: a.x / b.x, y: a.y / b.y };
        },
        flip: function (a: XY): XY {
            return { x: a.y, y: a.x };
        },
        flip_x: function (a: XY): XY { //90° clockwise
            return { x: -a.y, y: a.x };
        },
        flip_y: function (a: XY): XY {
            return { x: a.y, y: -a.x };
        },
        scale: function (a: XY, scale: number): XY {
            return { x: (a.x * scale) || 0, y: (a.y * scale) || 0 };
        },
        normalize: function (a: XY): XY {
            const length = Math.sqrt(a.x * a.x + a.y * a.y);
            if (length === 0) return { x: 0, y: 0 };
            return { x: a.x / length, y: a.y / length };
        },
        negative: function (a: XY): XY {
            return { x: a.x * -1, y: a.y * -1 };
        },
        positive: function (a: XY): XY {
            return { x: Math.abs(a.x), y: Math.abs(a.y) };
        },
        round: function (a: XY, decimal: number): XY {
            const dec = Math.pow(10, decimal);
            return { x: Math.round(a.x * dec) / dec, y: Math.round(a.y * dec) / dec };
        },
        trunc: function (a: XY, decimal: number): XY {
            const dec = Math.pow(10, decimal);
            return { x: Math.trunc(a.x * dec) / dec, y: Math.trunc(a.y * dec) / dec };
        },
        copy: function (a: XY): XY {
            return { x: a.x, y: a.y };
        },
        ease: function (a: XY, easing: string, pow?: number): XY {
            const easingFunc = Ease[easing as keyof typeof Ease];
            if (typeof easingFunc !== 'function') {
                console.warn(`Easing function '${easing}' not found`);
                return { x: a.x, y: a.y };
            }
            // Handle functions that take 1 or 2 parameters (like powIn, powOut, powInOut)
            const easedX = easingFunc.length === 2 && pow !== undefined
                ? (easingFunc as (t: number, pow: number) => number)(a.x, pow)
                : (easingFunc as (t: number) => number)(a.x);
            const easedY = easingFunc.length === 2 && pow !== undefined
                ? (easingFunc as (t: number, pow: number) => number)(a.y, pow)
                : (easingFunc as (t: number) => number)(a.y);
            return { x: easedX, y: easedY };
        }
    },
    get: {
        length: function (a: XY): number { //pytagoras... also called 'magnitude' sometimes
            return Math.sqrt(a.x * a.x + a.y * a.y);
        },
        dot: function (a: XY): number {
            return a.x * a.x + a.y * a.y;
        },
        middle: function (a: XY, b: XY): XY { //get middle direction
            const sum = { x: a.x + b.x, y: a.y + b.y };
            const magnitude = Math.sqrt(sum.x * sum.x + sum.y * sum.y);
            if (magnitude === 0) return { x: 0, y: 0 };
            return { x: sum.x / magnitude, y: sum.y / magnitude };
        },
        center: function (a: XY, b: XY): XY { //get middle position / or average
            return { x: (b.x + a.x) * .5, y: (b.y + a.y) * .5 };
        },
        distance: function (a: XY, b: XY): number {
            const aa = a.x - b.x;
            const bb = a.y - b.y;
            return Math.abs(Math.sqrt(aa * aa + bb * bb));
        },
        direction: function (a: XY, b: XY): XY { //returns normalised direction vector
            const dirVector = { x: b.x - a.x, y: b.y - a.y };
            const magnitude = V2.get.length(dirVector);
            if (magnitude === 0) return { x: 0, y: 0 };
            return { x: dirVector.x / magnitude, y: dirVector.y / magnitude };
        },
        mirror: function (a: XY, b: XY): XY {
            return { x: a.x + (b.x - a.x) * 2, y: a.y + (b.y - a.y) * 2 };
        },
        vector: function (radian: number): XY {
            return { x: Math.cos(radian), y: Math.sin(radian) };
        },
        radian: function (a: XY): number {
            return Math.atan2(a.y, a.x);
        },
        angle: function (a: XY, b: XY = { x: 0, y: 0 }, offset: number = 0.25): number { //returns normalised angle (-0.5 to 0.5), 0 is 12 o'clock
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const ra = Math.atan2(dy, dx) / V2.pp;
            let result = ra + offset;
            if (result > 0.5) result -= 1;
            if (result < -0.5) result += 1;
            return result;
        },
        perpendicular: function (a: XY, b: XY): XY {
            const dir = V2.get.direction(a, b);
            V2.flip_y(dir);
            return dir;
        }
    },
    zero: function (a: XY): void {
        a.x = 0;
        a.y = 0;
    },
    set: function (a: XY, b: XY): void {
        a.x = b.x;
        a.y = b.y;
    },
    set_x: function (a: XY, x: number): void {
        a.x = x;
    },
    set_y: function (a: XY, y: number): void {
        a.y = y;
    },
    sub: function (a: XY, b: XY): void {
        a.x -= b.x;
        a.y -= b.y;
    },
    sub_x: function (a: XY, x: number): void {
        a.x -= x;
    },
    sub_y: function (a: XY, y: number): void {
        a.y -= y;
    },
    add: function (a: XY, b: XY): void {
        a.x += b.x;
        a.y += b.y;
    },
    add_x: function (a: XY, x: number): void {
        a.x += x;
    },
    add_y: function (a: XY, y: number): void {
        a.y += y;
    },
    multiply: function (a: XY, b: XY): void {
        a.x *= b.x;
        a.y *= b.y;
    },
    divide: function (a: XY, b: XY): void {
        a.x /= b.x;
        a.y /= b.y;
    },
    flip: function (a: XY): void {
        const temp = a.x;
        a.x = a.y;
        a.y = temp;
    },
    flip_x: function (a: XY): void { //90° clockwise
        const temp = a.x;
        a.x = -a.y;
        a.y = temp;
    },
    flip_y: function (a: XY): void {
        const temp = a.y;
        a.y = -a.x;
        a.x = temp;
    },
    scale: function (a: XY, scale: number): void {
        a.x *= scale;
        a.y *= scale;
    },
    normalize: function (a: XY): void {
        const length = Math.sqrt(a.x * a.x + a.y * a.y);
        if (length === 0) {
            a.x = 0;
            a.y = 0;
        } else {
            a.x /= length;
            a.y /= length;
        }
    },
    negative: function (a: XY): void {
        a.x *= -1;
        a.y *= -1;
    },
    positive: function (a: XY): void {
        a.x = Math.abs(a.x);
        a.y = Math.abs(a.y);
    },
    round: function (a: XY, decimal: number): void {
        const dec = Math.pow(10, decimal);
        a.x = Math.round(a.x * dec) / dec;
        a.y = Math.round(a.y * dec) / dec;
    },
    trunc: function (a: XY, decimal: number): void {
        const dec = Math.pow(10, decimal);
        a.x = Math.trunc(a.x * dec) / dec;
        a.y = Math.trunc(a.y * dec) / dec;
    },
    ceil: function (a: XY): void {
        a.x = Math.ceil(a.x);
        a.y = Math.ceil(a.y);
    },
    floor: function (a: XY): void {
        a.x = Math.floor(a.x);
        a.y = Math.floor(a.y);
    },
    ease: function (a: XY, easing: string, pow?: number): void {
        const easingFunc = Ease[easing as keyof typeof Ease];
        if (typeof easingFunc !== 'function') {
            console.warn(`Easing function '${easing}' not found`);
            return;
        }
        // Handle functions that take 1 or 2 parameters (like powIn, powOut, powInOut)
        if (easingFunc.length === 2 && pow !== undefined) {
            a.x = (easingFunc as (t: number, pow: number) => number)(a.x, pow);
            a.y = (easingFunc as (t: number, pow: number) => number)(a.y, pow);
        } else {
            a.x = (easingFunc as (t: number) => number)(a.x);
            a.y = (easingFunc as (t: number) => number)(a.y);
        }
    }
};